import type { CSSProperties } from "react";
import { forwardRef, type ElementType } from "react";

import { flattenStyles, platformProps } from "../../lib";
import { ariaLabelFor } from "../../lib/aria-name";
import { getAriaState, getHeadingLevel } from "../../lib/aria-state";
import type { BoxProps } from "./types";

const ROLE_MAP: Record<NonNullable<BoxProps["accessibilityRole"]>, string | undefined> = {
  none: "presentation",
  button: "button",
  link: "link",
  search: "search",
  image: "img",
  // ARIA has no "text" role; the element stays generic and its content is read as-is
  text: undefined,
  header: "heading",
  alert: "alert",
  status: "status",
  menu: "menu",
  menuitem: "menuitem",
  list: "list",
  listitem: "listitem",
  tablist: "tablist",
  tab: "tab",
  group: "group",
  radiogroup: "radiogroup",
};

/**
 * Box - Web implementation
 *
 * Polymorphic wrapper that renders semantic HTML elements.
 * Transforms accessibility props to ARIA attributes.
 */
export const Box = forwardRef<HTMLElement, BoxProps>(
  (
    {
      children,
      className,
      testID,
      as = "div",
      accessibilityLabel,
      accessibilityHint,
      accessibilityRole,
      accessibilityState,
      accessible: _accessible, // Native-only grouping; no DOM equivalent
      pointerEvents,
      style,
      ...rest
    },
    ref,
  ) => {
    const Component = as as ElementType;

    const role = accessibilityRole ? ROLE_MAP[accessibilityRole] : undefined;
    // Map state against the role that renders: an explicit `role` wins in the
    // spread (even undefined), and with no role attribute an anchor with an
    // href is a link
    const explicit = rest as { role?: string | null };
    const renderedRole =
      ("role" in explicit ? explicit.role : role) ??
      (as === "a" && rest.href !== undefined ? "link" : undefined);

    const pointerEventsValue =
      pointerEvents === "none" ? "none" : pointerEvents === "auto" ? "auto" : undefined;

    const mergedStyle: CSSProperties | undefined =
      style || pointerEventsValue
        ? {
            ...(style ? flattenStyles([style]) : undefined),
            ...(pointerEventsValue && { pointerEvents: pointerEventsValue }),
          }
        : undefined;

    return (
      <Component
        ref={ref}
        className={className}
        {...platformProps({ testID })}
        aria-label={ariaLabelFor(
          "Box",
          accessibilityLabel,
          "role" in explicit ? explicit.role : role,
          as,
          rest.href !== undefined,
        )}
        aria-description={accessibilityHint}
        role={role}
        {...getHeadingLevel(renderedRole, as)}
        aria-disabled={accessibilityState?.disabled}
        {...getAriaState(renderedRole, accessibilityState)}
        style={mergedStyle}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

Box.displayName = "Box";
